'use strict';

import { PROJECT_API, JSON_HEADERS } from './projectApi.js';

/**
 * "Choose from project images" browser: lists every image already uploaded
 * for a project (drafts and images attached to other updates alike) and lets
 * the user attach one to the update currently being edited.
 *
 * Picking an image already attached elsewhere duplicates it server-side
 * (see ProjectImageService::resolveForAttach()) rather than moving it, so the
 * trash affordance is only offered for unattached (draft) tiles — that
 * mirrors image_delete.php's 422-on-attached rule, so the UI never offers an
 * action the server will refuse.
 */
export function createImagePicker({ projectId, csrfToken, uploadManager, openBtn, panel, grid, errorEl }) {
  let loaded = false;

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  }

  function hideError() {
    if (!errorEl) return;
    errorEl.style.display = 'none';
  }

  function renderTile(img) {
    const tile = document.createElement('div');
    tile.className = 'proj-img-picker__tile';
    tile.title = img.attached ? (img.update_title || 'Attached') : 'Draft';

    const thumb = document.createElement('img');
    thumb.className = 'proj-img-picker__thumb';
    thumb.src = img.url;
    thumb.alt = '';
    tile.appendChild(thumb);

    const badge = document.createElement('span');
    badge.className = 'proj-img-picker__badge';
    badge.textContent = img.attached ? (img.update_title || 'Attached') : 'Draft';
    tile.appendChild(badge);

    tile.addEventListener('click', () => {
      hideError();
      // alt_text comes from image_list.php — carry the description forward so
      // reusing a picture does not silently drop the one already written for it.
      const ok = uploadManager.attachExisting({ id: img.id, url: img.url, alt: img.alt_text || '' });
      if (!ok) return;
    });

    if (!img.attached) {
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'proj-img-picker__delete';
      del.setAttribute('aria-label', 'Delete image');
      del.innerHTML = '<i class="fa-solid fa-trash"></i>';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        deleteImage(img.id, tile);
      });
      tile.appendChild(del);
    }

    return tile;
  }

  function deleteImage(id, tile) {
    const fd = new FormData();
    fd.append('image_id', id);
    fd.append('csrf_token', csrfToken);
    fetch(PROJECT_API.imageDelete, {
      method: 'POST', credentials: 'same-origin', headers: JSON_HEADERS, body: fd,
    })
      .then(r => r.json())
      .then(envelope => {
        if (envelope.return_code !== '1') throw new Error(envelope.error || 'Server error');
        tile.remove();
      })
      .catch(err => showError(err.message || 'Failed to delete image.'));
  }

  function refresh() {
    grid.innerHTML = '<p class="proj-muted">Loading…</p>';
    return fetch(`${PROJECT_API.imageList}?project_id=${encodeURIComponent(projectId)}`, {
      credentials: 'same-origin', headers: JSON_HEADERS,
    })
      .then(r => r.json())
      .then(envelope => {
        if (envelope.return_code !== '1') throw new Error(envelope.error || 'Server error');
        grid.innerHTML = '';
        const images = envelope.data.images || [];
        if (images.length === 0) {
          grid.innerHTML = '<p class="proj-muted">No images uploaded for this project yet.</p>';
          return;
        }
        images.forEach(img => grid.appendChild(renderTile(img)));
      })
      .catch(err => {
        grid.innerHTML = '';
        showError(err.message || 'Failed to load images.');
      });
  }

  function open() {
    panel.classList.remove('is-hidden');
    hideError();
    if (!loaded) {
      loaded = true;
      refresh();
    }
  }

  function close() {
    panel.classList.add('is-hidden');
  }

  openBtn.addEventListener('click', () => {
    if (panel.classList.contains('is-hidden')) open(); else close();
  });
  panel.querySelectorAll('.js-img-picker-close').forEach(btn => btn.addEventListener('click', close));
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !panel.classList.contains('is-hidden')) close();
  });

  return { open, close, refresh };
}
